import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import type { LocalizationSettings } from './localizationApi';
import type { AuthUser, TenantMembership } from '../stores/authStore';
import { DEFAULT_LOCALIZATION } from '../lib/constants/localizationDefaults';
import type { BillingAccessSummary } from '../types/billing';

// ── DTOs ──────────────────────────────────────────────────────────────────────

/** Mirrors Beetegre.Shared.DTOs.Auth.PasswordPolicyResponse */
export interface PasswordPolicyResponse {
  minLength: number;
  longBypassLength: number;
  maxLength: number;
}

/**
 * Response shape shared by `POST /auth/login` and `POST /auth/switch-tenant`
 * (AB#1470/#1471) — switching tenants mints a fresh session, so the server
 * returns the same envelope either way rather than a partial patch.
 */
export interface AuthSessionResponse {
  accessToken: string;
  accessTokenExpiresInSeconds: number;
  role: 'SuperAdmin' | 'TenantAdmin' | 'TenantMember';
  fullName: string;
  email: string;
  tenantId: string | null;
  tenantSlug: string | null;
  activeModules: string[];
  permissions: string[];
  localizationSettings: LocalizationSettings;
  memberships: TenantMembership[];
  /** Null for SuperAdmin / a tenant-less user (contract: docs/design/billing-access-gate.md). */
  billing?: BillingAccessSummary | null;
}

/**
 * Maps the shared login / switch-tenant envelope onto the `authStore.setAuth` payload.
 *
 * Single source of truth for that mapping (AB#1471 PR #2244 review, thread #6290): before
 * this, `LoginPage.tsx` and `useSwitchTenant.ts` each hand-wrote the identical nine-field
 * mapping, including the `permissions ?? []` / `localizationSettings ?? DEFAULT_LOCALIZATION`
 * / `memberships ?? []` fallbacks — a duplication born in this same PR (`memberships` had to
 * be added to both). A field added to one and not the other would make login and switch-tenant
 * silently produce different `AuthUser` shapes, and the gap would only surface after a switch.
 */
export function toAuthUser(data: AuthSessionResponse): AuthUser {
  return {
    email: data.email,
    fullName: data.fullName,
    role: data.role,
    tenantId: data.tenantId,
    tenantSlug: data.tenantSlug,
    activeModules: data.activeModules,
    permissions: data.permissions ?? [],
    localizationSettings: data.localizationSettings ?? DEFAULT_LOCALIZATION,
    memberships: data.memberships ?? [],
    billing: data.billing ?? null,
  };
}

/**
 * `POST /auth/refresh` response (mirrors Beetegre.Shared.DTOs.Auth.RefreshResponse). Consumed by
 * `tryRefresh()` in lib/api.ts, which mirrors `permissions`/`memberships`/`localizationSettings`/
 * `billing` into authStore on every silent token rotation — see the field-by-field rationale on
 * `toAuthUser` above; the same "one field added, both sides must know" risk applies here.
 */
export interface RefreshResponse {
  accessToken: string;
  accessTokenExpiresInSeconds: number;
  permissions: string[];
  localizationSettings: LocalizationSettings;
  memberships: TenantMembership[];
  /** Null for SuperAdmin / a tenant-less user (contract: docs/design/billing-access-gate.md). */
  billing?: BillingAccessSummary | null;
}

// ── Query-key factory ─────────────────────────────────────────────────────────

export const authKeys = {
  passwordPolicy: ['auth', 'password-policy'] as const,
};

// ── Raw API calls ─────────────────────────────────────────────────────────────

export const authApi = {
  getPasswordPolicy: (): Promise<PasswordPolicyResponse> =>
    api.get<PasswordPolicyResponse>('/api/v1/auth/password-policy').then((r) => r.data),

  /**
   * Switches the active tenant for the current session. Server-verified measurement
   * (2026-08-10): 200 mints a new access token (rotated `jti`) and rotates the refresh
   * cookie; 403 with `detail: "auth.switchTenant.membership.notFound"` when the caller
   * has no live membership in the target tenant; 401 when the bearer token is missing
   * or invalid (the shared axios interceptor already retries once via silent refresh
   * before this ever surfaces to a caller).
   */
  switchTenant: (tenantId: string): Promise<AuthSessionResponse> =>
    api
      .post<AuthSessionResponse>('/api/v1/auth/switch-tenant', { tenantId })
      .then((r) => r.data),
};

// ── TanStack Query hooks ──────────────────────────────────────────────────────

/**
 * Fetches password policy from the backend — the single source of truth.
 * The endpoint is anonymous (reachable before login), so this can be called
 * on registration, reset-password, and profile-change pages without auth.
 *
 * staleTime=Infinity: policy constants change only on backend deploys, so
 * once fetched per session there is no value in re-fetching.
 */
export function usePasswordPolicy() {
  return useQuery<PasswordPolicyResponse>({
    queryKey: authKeys.passwordPolicy,
    queryFn: authApi.getPasswordPolicy,
    staleTime: Infinity,
  });
}
